import { ReactNode } from "react";
import { CaretUp, CaretDown } from "@phosphor-icons/react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@libs/zapi-ui/shadcn";
import { cn } from "@/zapi-frontend/shared/lib/utils";

type SidebarButtonProps = {
  icon?: ReactNode;
  label: string;
  active?: boolean;
  collapsed?: boolean;
  caret?: "up" | "down";
  right?: ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  testId?: string;
  className?: string;
};

export function SidebarButton({
  icon,
  label,
  active,
  collapsed,
  caret,
  right,
  disabled,
  onClick,
  testId,
  className
}: SidebarButtonProps) {
  return (
    <Tooltip open={collapsed ? undefined : false}>
      <TooltipTrigger asChild>
        <button
          type="button"
          data-testid={testId}
          disabled={disabled}
          onClick={onClick}
          className={cn(
            "flex items-center gap-2 min-h-8 w-full p-2 rounded-lg text-left transition-colors outline-none focus:outline-none focus-visible:outline-none",
            "text-[14px] leading-4 font-medium text-innoveraZinc-600 hover:bg-innoveraZinc-50",
            active && "bg-innoveraZinc-100 text-innoveraZinc-900",
            disabled && "text-innoveraZinc-400 hover:bg-transparent cursor-default",
            className
          )}
        >
          {icon && <span className="shrink-0 inline-flex">{icon}</span>}
          <span
            className={cn(
              "flex-1 min-w-0 transition-[opacity] duration-200",
              collapsed ? "truncate opacity-0 max-w-0 flex-none" : "line-clamp-2"
            )}
          >
            {label}
          </span>
          {!collapsed && right}
          {!collapsed &&
            caret &&
            (caret === "up" ? (
              <CaretUp className="size-4 shrink-0" weight="regular" />
            ) : (
              <CaretDown className="size-4 shrink-0" weight="regular" />
            ))}
        </button>
      </TooltipTrigger>
      <TooltipContent side="right">{label}</TooltipContent>
    </Tooltip>
  );
}

export function SidebarGroupLabel({ label, collapsed }: { label: string; collapsed?: boolean }) {
  return (
    <div className="flex items-center h-8 px-2">
      {collapsed ? (
        <span className="w-full h-px bg-innoveraZinc-150" />
      ) : (
        <span className="block overflow-hidden whitespace-nowrap text-[10px] leading-4 font-medium uppercase tracking-[0.6px] text-innoveraZinc-500">
          {label}
        </span>
      )}
    </div>
  );
}

export function SidebarSubMenu({ expanded, children }: { expanded: boolean; children: ReactNode }) {
  return (
    <div
      className={cn(
        "grid transition-all duration-300 ease-in-out",
        expanded ? "grid-rows-[1fr]" : "grid-rows-[0fr]"
      )}
    >
      <div className="overflow-hidden">
        <div className="relative flex flex-col gap-px pl-8 py-1">
          <div className="absolute left-4 top-0 bottom-0 w-px bg-innoveraZinc-150" />
          {children}
        </div>
      </div>
    </div>
  );
}
